import { Badge, Button, EmptyState, Spinner } from '@axioma/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  CheckCircle2,
  ChevronRight,
  Clock,
  FileText,
  Gavel,
  Inbox as InboxIcon,
  RefreshCw,
} from 'lucide-react';
import { useState } from 'react';

import { PageHeader } from '../components/PageHeader.js';
import { api } from '../lib/api.js';
import { useAuthStore } from '../stores/auth.js';

interface InboxItem {
  id: string;
  processId: string;
  process: {
    id: string;
    cnjNumber: string;
    cnjNumberMasked: string;
    court: string;
    primarySubject: string | null;
    processClass: string | null;
  };
  source: 'DJEN' | 'DATAJUD' | 'MNI' | 'EPROC' | 'DJE_LOCAL' | 'EMAIL' | 'MANUAL' | 'CODEX';
  movementType: string | null;
  rawText: string;
  publishedAt: string;
  intimationKnownAt: string | null;
  requiresAction: boolean;
  read: boolean;
  attachments: unknown;
}

interface InboxResponse {
  items: InboxItem[];
  total: number;
  unreadCount: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export function Inbox(): JSX.Element {
  const tenant = useAuthStore((s) => s.currentTenantId);
  const [selected, setSelected] = useState<InboxItem | null>(null);
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['vigilia', 'inbox', tenant],
    queryFn: async () => {
      const { data } = await api.get<InboxResponse>('/vigilia/inbox', {
        params: { pageSize: 100 },
      });
      return data;
    },
    enabled: Boolean(tenant),
    refetchInterval: 60_000,
  });

  const markRead = useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/vigilia/inbox/${id}/read`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vigilia', 'inbox'] }),
  });

  const refresh = useMutation({
    mutationFn: async () => {
      // Sync DJEN nos últimos 30 dias
      const desde = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
      try {
        await api.post('/djen/sync', { desde });
      } catch {
        // se OAB não cadastrada, ignora — só atualiza lista
      }
      await qc.invalidateQueries({ queryKey: ['vigilia', 'inbox'] });
    },
  });

  return (
    <div>
      <PageHeader
        eyebrow="Vigília"
        title="Inbox de intimações"
        description="Comunicações DJEN/DataJud que requerem ação. Click para ler integralmente."
        actions={
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<RefreshCw size={14} />}
            loading={refresh.isPending}
            onClick={() => refresh.mutate()}
          >
            Atualizar
          </Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr]">
        {/* Lista de itens */}
        <div className="border-r border-ax-line">
          {query.isLoading ? (
            <div className="flex items-center justify-center px-8 py-16">
              <Spinner size="md" variant="aurora" />
            </div>
          ) : (query.data?.items.length ?? 0) === 0 ? (
            <div className="px-6 py-12">
              <EmptyState
                icon={<InboxIcon size={36} />}
                title="Sem intimações pendentes"
                description="Cadastre sua OAB e importe processos para começar a receber comunicações automaticamente."
              />
            </div>
          ) : (
            <ul className="divide-y divide-ax-line">
              {(query.data?.items ?? []).map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setSelected(item);
                      if (!item.read) markRead.mutate(item.id);
                    }}
                    className={[
                      'group flex w-full items-start gap-3 px-5 py-4 text-left transition-colors',
                      selected?.id === item.id ? 'bg-ax-night' : 'hover:bg-ax-night/50',
                      !item.read
                        ? 'border-l-2 border-l-ax-aurora'
                        : 'border-l-2 border-l-transparent',
                    ].join(' ')}
                  >
                    <div
                      className={[
                        'mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-ax-sm border',
                        item.movementType?.startsWith('INTIMACAO_') ||
                        item.movementType === 'CITACAO'
                          ? 'border-[rgba(229,165,71,0.30)] bg-[rgba(229,165,71,0.08)] text-ax-aurora'
                          : item.movementType === 'SENTENCA' ||
                              item.movementType === 'ACORDAO' ||
                              item.movementType === 'PUBLICACAO_DECISAO'
                            ? 'border-[rgba(110,91,255,0.30)] bg-[rgba(110,91,255,0.08)] text-ax-plasma'
                            : 'border-ax-line bg-ax-night text-ax-text-tertiary',
                      ].join(' ')}
                    >
                      {item.movementType?.startsWith('INTIMACAO_') ||
                      item.movementType === 'CITACAO' ? (
                        <AlertCircle size={16} />
                      ) : item.movementType === 'SENTENCA' ||
                        item.movementType === 'ACORDAO' ||
                        item.movementType === 'PUBLICACAO_DECISAO' ? (
                        <Gavel size={16} />
                      ) : (
                        <FileText size={16} />
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-mono text-[11px] uppercase tracking-wider text-ax-text-tertiary">
                          {item.process.court} · {item.source}
                        </p>
                        {!item.read && (
                          <span
                            className="h-1.5 w-1.5 rounded-full bg-ax-aurora"
                            aria-label="Não lida"
                          />
                        )}
                      </div>
                      <p
                        className={[
                          'mt-0.5 truncate font-display text-sm',
                          item.read ? 'text-ax-text-secondary' : 'text-ax-text-primary',
                        ].join(' ')}
                      >
                        {humanizeMovementType(item.movementType)}
                      </p>
                      <p className="mt-1 font-mono text-[11px] text-ax-text-tertiary">
                        {item.process.cnjNumberMasked}
                      </p>
                      <p className="mt-1 line-clamp-2 text-[12px] text-ax-text-tertiary">
                        {item.rawText.replace(/^\[.+?\]\s*/, '').slice(0, 120)}…
                      </p>
                      <div className="mt-1.5 flex items-center gap-2">
                        <Clock size={11} className="text-ax-text-tertiary" />
                        <span className="font-mono text-[10px] text-ax-text-tertiary">
                          {new Date(item.publishedAt).toLocaleDateString('pt-BR', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </span>
                      </div>
                    </div>
                    <ChevronRight
                      size={14}
                      className="mt-3 flex-shrink-0 text-ax-text-tertiary opacity-0 transition-opacity group-hover:opacity-100"
                    />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Detalhe */}
        <div className="px-8 py-6">
          {selected ? (
            <article className="max-w-3xl">
              <div className="mb-4 flex items-center gap-2">
                <Badge variant={badgeVariantFor(selected.movementType)} uppercase>
                  {humanizeMovementType(selected.movementType)}
                </Badge>
                <Badge variant="neutral" uppercase>
                  {selected.source}
                </Badge>
                <Badge variant="neutral" uppercase>
                  {selected.process.court}
                </Badge>
                {selected.read && (
                  <span className="ml-auto inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-wider text-ax-text-tertiary">
                    <CheckCircle2 size={12} className="text-ax-electric" /> Lida
                  </span>
                )}
              </div>
              <h2 className="font-display text-2xl tracking-tight text-ax-text-primary">
                {selected.process.cnjNumberMasked}
              </h2>
              <p className="ax-body-sm mt-1 text-ax-text-secondary">
                {selected.process.processClass ?? '—'}
                {selected.process.primarySubject ? ` · ${selected.process.primarySubject}` : ''}
              </p>
              <p className="mt-3 font-mono text-[11px] uppercase tracking-wider text-ax-text-tertiary">
                Publicado em{' '}
                {new Date(selected.publishedAt).toLocaleDateString('pt-BR', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                })}
              </p>
              <div className="mt-6 rounded-ax-md border border-ax-line bg-ax-graphite p-5">
                <pre className="whitespace-pre-wrap font-sans text-[14px] leading-relaxed text-ax-text-primary">
                  {selected.rawText.replace(/^\[.+?\]\s*/, '')}
                </pre>
              </div>
            </article>
          ) : (
            <div className="flex h-full items-center justify-center">
              <p className="ax-body text-ax-text-tertiary">
                Selecione uma intimação para ler o texto integral
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function humanizeMovementType(t: string | null): string {
  if (!t) return 'Movimento';
  const map: Record<string, string> = {
    INTIMACAO_PARA_RESPOSTA: 'Intimação para resposta',
    INTIMACAO_PARA_CONTESTAR: 'Intimação para contestar',
    INTIMACAO_PARA_MANIFESTAR: 'Intimação para manifestar',
    INTIMACAO_PARA_REPLICAR: 'Intimação para replicar',
    INTIMACAO_PARA_TRIPLICAR: 'Intimação para triplicar',
    INTIMACAO_PARA_RAZOES_FINAIS: 'Intimação para razões finais',
    INTIMACAO_PERICIA: 'Intimação para perícia',
    INTIMACAO_AUDIENCIA: 'Intimação de audiência',
    INTIMACAO_PARA_PAGAMENTO: 'Intimação para pagamento',
    INTIMACAO_PARA_CUMPRIMENTO_SENTENCA: 'Cumprimento de sentença',
    INTIMACAO_DE_LIMINAR: 'Intimação de liminar',
    INTIMACAO_PARA_CONTRARRAZOES_RECURSO: 'Contrarrazões de recurso',
    PUBLICACAO_DECISAO: 'Publicação de decisão',
    DESPACHO_INFORMATIVO: 'Despacho',
    CITACAO: 'Citação',
    SENTENCA: 'Sentença',
    ACORDAO: 'Acórdão',
    DECISAO_INTERLOCUTORIA: 'Decisão interlocutória',
    CIENCIA: 'Ciência',
    OUTRO: 'Movimento',
  };
  return map[t] ?? t.replace(/_/g, ' ').toLowerCase();
}

function badgeVariantFor(t: string | null): 'aurora' | 'plasma' | 'electric' | 'neutral' {
  if (!t) return 'neutral';
  if (t.startsWith('INTIMACAO_') || t === 'CITACAO') return 'aurora';
  if (t === 'SENTENCA' || t === 'ACORDAO' || t === 'PUBLICACAO_DECISAO') return 'plasma';
  return 'neutral';
}
